import React, { useEffect, useState, useCallback } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Table,
  Input,
  Menu,
  Label,
  Loader,
  Button,
  Pagination,
  Card,
  Icon,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../../api';

const PAGE_SIZE = 50;

const SEVERITY_COLORS = {
  critical: 'red',
  high: 'orange',
  medium: 'yellow',
  low: 'blue',
  info: 'grey',
};

const STATUS_COLORS = {
  open: 'red',
  investigating: 'orange',
  dispositioned: 'yellow',
  resolving: 'teal',
  verification: 'blue',
  deferred: 'grey',
  closed: 'green',
};

function formatAgeDays(days) {
  if (days == null) return '—';
  const d = parseFloat(days);
  if (d > 365) return `${Math.floor(d / 365)}y ${Math.round(d % 365)}d`;
  if (d >= 1) return `${Math.round(d)}d`;
  return `${Math.round(d * 24)}h`;
}

export default function IssueTracker() {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const [severity, setSeverity] = useState(params.get('severity') || '');
  const [status, setStatus] = useState(params.get('status') || '');
  const [problemType, setProblemType] = useState(params.get('problem_type') || '');
  const [search, setSearch] = useState(params.get('search') || '');
  const [searchInput, setSearchInput] = useState(params.get('search') || '');
  const [page, setPage] = useState(parseInt(params.get('page'), 10) || 1);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (severity) qs.set('severity', severity);
    if (status) qs.set('status', status);
    if (problemType) qs.set('problem_type', problemType);
    if (search) qs.set('search', search);
    qs.set('page', page);
    qs.set('page_size', PAGE_SIZE);

    fetchApi(`${getApiPath()}/@qms/issues?${qs}`)
      .then((json) => {
        setData(json);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [severity, status, problemType, search, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sync URL
  useEffect(() => {
    const qs = new URLSearchParams();
    if (severity) qs.set('severity', severity);
    if (status) qs.set('status', status);
    if (problemType) qs.set('problem_type', problemType);
    if (search) qs.set('search', search);
    if (page > 1) qs.set('page', page);
    const str = qs.toString();
    history.replace(`/qms/issues${str ? `?${str}` : ''}`);
  }, [severity, status, problemType, search, page, history]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const clearFilters = () => {
    setSeverity('');
    setStatus('');
    setProblemType('');
    setSearch('');
    setSearchInput('');
    setPage(1);
  };

  const totalPages = data ? Math.ceil(data.items_total / PAGE_SIZE) : 0;
  const summary = data?.summary || {};

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '3em' }}>
      <Helmet title="Issue Tracker" />
      <Link to="/qms">&larr; QMS Dashboard</Link>

      <div className="polyglot-page-header">
        <h1>Issue Tracker</h1>
        <p className="subtitle">
          {data ? `${data.items_total} problem reports` : ''}
        </p>
      </div>

      {/* Summary cards */}
      {data && (
        <Card.Group itemsPerRow={4} stackable style={{ marginBottom: '1.5em' }}>
          <Card>
            <Card.Content>
              <Card.Header>{summary.total || 0}</Card.Header>
              <Card.Meta>Total Issues</Card.Meta>
            </Card.Content>
          </Card>
          <Card>
            <Card.Content>
              <Label color="red" style={{ float: 'right' }}>
                {summary.open || 0}
              </Label>
              <Card.Header>Open</Card.Header>
            </Card.Content>
          </Card>
          <Card>
            <Card.Content>
              <Label color="green" style={{ float: 'right' }}>
                {summary.closed || 0}
              </Label>
              <Card.Header>Closed</Card.Header>
            </Card.Content>
          </Card>
          <Card>
            <Card.Content>
              <Label color="orange" style={{ float: 'right' }}>
                {summary.high_severity_open || 0}
              </Label>
              <Card.Header>High/Critical Open</Card.Header>
            </Card.Content>
          </Card>
        </Card.Group>
      )}

      <div style={{ display: 'flex', gap: '1.5em' }}>
        {/* Sidebar filters */}
        <div style={{ width: '200px', flexShrink: 0 }}>
          {/* Severity filter */}
          <Header as="h5" style={{ marginBottom: '0.3em' }}>
            Severity
          </Header>
          <Menu vertical fluid size="small">
            <Menu.Item
              active={!severity}
              onClick={() => { setSeverity(''); setPage(1); }}
            >
              All severities
            </Menu.Item>
            {(data?.severities || [])
              .filter((s) => s.count > 0)
              .map((s) => (
                <Menu.Item
                  key={s.code}
                  active={severity === s.code}
                  onClick={() => { setSeverity(s.code); setPage(1); }}
                >
                  {s.label}
                  <Label size="tiny" color={SEVERITY_COLORS[s.code] || 'grey'}>
                    {s.count}
                  </Label>
                </Menu.Item>
              ))}
          </Menu>

          {/* Status filter */}
          <Header as="h5" style={{ marginBottom: '0.3em', marginTop: '1em' }}>
            Status
          </Header>
          <Menu vertical fluid size="small">
            <Menu.Item
              active={!status}
              onClick={() => { setStatus(''); setPage(1); }}
            >
              All statuses
            </Menu.Item>
            {(data?.statuses || [])
              .filter((s) => s.count > 0)
              .map((s) => (
                <Menu.Item
                  key={s.code}
                  active={status === s.code}
                  onClick={() => { setStatus(s.code); setPage(1); }}
                >
                  {s.label}
                  <Label size="tiny" color={STATUS_COLORS[s.code] || 'grey'}>
                    {s.count}
                  </Label>
                </Menu.Item>
              ))}
          </Menu>

          {/* Problem Type filter */}
          <Header as="h5" style={{ marginBottom: '0.3em', marginTop: '1em' }}>
            Type
          </Header>
          <Menu vertical fluid size="small">
            <Menu.Item
              active={!problemType}
              onClick={() => { setProblemType(''); setPage(1); }}
            >
              All types
            </Menu.Item>
            {(data?.types || [])
              .filter((t) => t.count > 0)
              .map((t) => (
                <Menu.Item
                  key={t.code}
                  active={problemType === t.code}
                  onClick={() => { setProblemType(t.code); setPage(1); }}
                >
                  {t.label}
                  <Label size="tiny">{t.count}</Label>
                </Menu.Item>
              ))}
          </Menu>

          {(severity || status || problemType || search) && (
            <Button
              fluid
              size="tiny"
              style={{ marginTop: '1em' }}
              onClick={clearFilters}
            >
              Clear Filters
            </Button>
          )}
        </div>

        {/* Main content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Search bar */}
          <Segment secondary size="small" style={{ marginBottom: '1em' }}>
            <Input
              fluid
              icon="search"
              placeholder="Search issues by title or ID..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              action={{
                content: 'Search',
                onClick: handleSearch,
              }}
            />
          </Segment>

          {loading ? (
            <Loader active inline="centered" />
          ) : error ? (
            <Segment color="red">Error: {error}</Segment>
          ) : (
            <>
              <Table compact celled selectable>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Report ID</Table.HeaderCell>
                    <Table.HeaderCell>Title</Table.HeaderCell>
                    <Table.HeaderCell>Severity</Table.HeaderCell>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                    <Table.HeaderCell>Type</Table.HeaderCell>
                    <Table.HeaderCell>Component</Table.HeaderCell>
                    <Table.HeaderCell>Reported</Table.HeaderCell>
                    <Table.HeaderCell>Age / SLA</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {(data.items || []).map((pr) => (
                    <Table.Row key={pr.report_id}>
                      <Table.Cell>
                        <Link to={`/qms/issues/${encodeURIComponent(pr.report_id)}`}>
                          <strong>{pr.report_id}</strong>
                        </Link>
                      </Table.Cell>
                      <Table.Cell>{pr.title}</Table.Cell>
                      <Table.Cell>
                        <Label size="tiny" color={SEVERITY_COLORS[pr.severity] || 'grey'}>
                          {pr.severity}
                        </Label>
                      </Table.Cell>
                      <Table.Cell>
                        <Label size="tiny" color={STATUS_COLORS[pr.status] || 'grey'}>
                          {pr.status}
                        </Label>
                      </Table.Cell>
                      <Table.Cell>{pr.problem_type}</Table.Cell>
                      <Table.Cell>{pr.affected_component || '—'}</Table.Cell>
                      <Table.Cell>
                        {new Date(pr.reported_at).toLocaleDateString()}
                      </Table.Cell>
                      <Table.Cell>
                        {formatAgeDays(pr.age_days)}
                        {pr.is_open && (
                          <Label
                            size="mini"
                            color={pr.sla_status === 'OVERDUE' ? 'red' : 'green'}
                            style={{ marginLeft: '0.5em' }}
                          >
                            {pr.sla_status}
                          </Label>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                  {(data.items || []).length === 0 && (
                    <Table.Row>
                      <Table.Cell colSpan="8" textAlign="center">
                        No issues found.
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>

              {totalPages > 1 && (
                <div style={{ textAlign: 'center', marginTop: '1em' }}>
                  <Pagination
                    activePage={page}
                    totalPages={totalPages}
                    onPageChange={(e, { activePage }) => setPage(activePage)}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Container>
  );
}
